import * as React from "react";
import {
  type BrowserStorageOptions,
  useBrowserStorage,
  useOrganizationLocalStorage,
  useOrganizationSessionStorage,
} from "./use-browser-storage";

// Re-export the type for consumers
export type { BrowserStorageOptions };

/**
 * Event-based change tracking for saved views
 *
 * These hooks emit custom events when values change (unless loading a view).
 * The SavedViewContext listens to these events to manage the hasUnsavedChanges flag.
 *
 * Benefits:
 * - No complex state management or rerenders
 * - No data duplication (no baseline storage)
 * - Clear separation of concerns
 * - Works both inside and outside SavedViewProvider
 */

// Global flag to track if we're loading a view
declare global {
  interface Window {
    __isLoadingView?: boolean;
  }
}

// Custom event for view configuration changes
export const VIEW_CONFIG_CHANGED_EVENT = "viewConfigChanged" as const;
export interface ViewConfigChangedEvent extends CustomEvent {
  detail: {
    key: string;
    value: unknown;
    storageType: "localStorage" | "sessionStorage";
  };
}

// Generic tracked browser storage hook
export function useTrackedBrowserStorage<T>(
  key: string,
  defaultValue: T,
  options: BrowserStorageOptions<T>,
) {
  const [value, setValue] = useBrowserStorage(key, defaultValue, options);

  const trackedSetValue = React.useCallback(
    (newValue: T | ((prev: T) => T)) => {
      // Calculate the actual new value
      const actualNewValue =
        typeof newValue === "function"
          ? (newValue as (prev: T) => T)(value)
          : newValue;

      // Check if value is actually changing
      const isActuallyChanging =
        JSON.stringify(actualNewValue) !== JSON.stringify(value);

      // Always update the value (for consistency with useState behavior)
      setValue(newValue);

      // Only emit event if value actually changed and not loading
      if (!window.__isLoadingView && isActuallyChanging) {
        // Small delay to ensure storage is updated first
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent(VIEW_CONFIG_CHANGED_EVENT, {
              detail: {
                key,
                value: actualNewValue,
                storageType: options.storage,
              },
            }),
          );
        }, 0);
      }
    },
    [key, value, setValue, options.storage],
  );

  return [value, trackedSetValue] as const;
}

// Session storage hook with tracking
export function useTrackedSessionStorage<T>(
  key: string,
  defaultValue: T,
  options?: Omit<BrowserStorageOptions<T>, "storage">,
) {
  const [value, setValue] = useOrganizationSessionStorage(
    key,
    defaultValue,
    options,
  );

  const trackedSetValue = React.useCallback(
    (newValue: T | ((prev: T) => T)) => {
      // Calculate the actual new value
      const actualNewValue =
        typeof newValue === "function"
          ? (newValue as (prev: T) => T)(value)
          : newValue;

      // Check if value is actually changing
      const isActuallyChanging =
        JSON.stringify(actualNewValue) !== JSON.stringify(value);

      // Always update the value (for consistency with useState behavior)
      setValue(newValue);

      // Only emit event if value actually changed and not loading
      if (!window.__isLoadingView && isActuallyChanging) {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent(VIEW_CONFIG_CHANGED_EVENT, {
              detail: {
                key,
                value: actualNewValue,
                storageType: "sessionStorage",
              },
            }),
          );
        }, 0);
      }
    },
    [key, value, setValue],
  );

  return [value, trackedSetValue] as const;
}

// Local storage hook with tracking
export function useTrackedLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: Omit<BrowserStorageOptions<T>, "storage">,
) {
  const [value, setValue] = useOrganizationLocalStorage(
    key,
    defaultValue,
    options,
  );

  const trackedSetValue = React.useCallback(
    (newValue: T | ((prev: T) => T)) => {
      // Calculate the actual new value
      const actualNewValue =
        typeof newValue === "function"
          ? (newValue as (prev: T) => T)(value)
          : newValue;

      // Check if value is actually changing
      const isActuallyChanging =
        JSON.stringify(actualNewValue) !== JSON.stringify(value);

      // Always update the value (for consistency with useState behavior)
      setValue(newValue);

      // Only emit event if value actually changed and not loading
      if (!window.__isLoadingView && isActuallyChanging) {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent(VIEW_CONFIG_CHANGED_EVENT, {
              detail: {
                key,
                value: actualNewValue,
                storageType: "localStorage",
              },
            }),
          );
        }, 0);
      }
    },
    [key, value, setValue],
  );

  return [value, trackedSetValue] as const;
}
